"use client";

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useAutoSwapSettings } from "@/hooks/useAutoSwapSettings";

export interface CanvasAutoSwapCoverage {
  canAutoSwapGas: boolean;
  autoSwapEnabled: boolean;
  loading: boolean;
}

export interface CanvasGasDataValue {
  autoSwapCoverage: CanvasAutoSwapCoverage;
}

export const AUTO_SWAP_COVERAGE_DISABLED: CanvasAutoSwapCoverage = Object.freeze({
  canAutoSwapGas: false,
  autoSwapEnabled: false,
  loading: false,
});

const DEFAULT_VALUE: CanvasGasDataValue = Object.freeze({
  autoSwapCoverage: AUTO_SWAP_COVERAGE_DISABLED,
});

const CanvasGasDataContext = createContext<CanvasGasDataValue>(DEFAULT_VALUE);

/**
 * Canvas-level provider for auto-swap state.
 *
 * Uses useAutoSwapSettings (settings-only, no balance polling) instead of
 * useAutoSwapCoverage (which triggers useAggregatedWalletBalances → 5s
 * polling for every candidate wallet in the org).
 *
 * On the canvas we treat "auto-swap enabled" as sufficient to suppress gas
 * warnings — the full eligibility check (balance ≥ $0.03 in a candidate
 * source wallet) only matters at execution time and is handled by the
 * backend's analyze-funds endpoint + the runtime auto-swap executor.
 */
export function CanvasGasDataProvider({ children }: { children: ReactNode }) {
  const { settings, loading } = useAutoSwapSettings();
  const autoSwapEnabled = Boolean(settings?.enabled);

  const value = useMemo<CanvasGasDataValue>(
    () => ({
      autoSwapCoverage: {
        canAutoSwapGas: autoSwapEnabled,
        autoSwapEnabled,
        loading,
      },
    }),
    [autoSwapEnabled, loading],
  );

  return <CanvasGasDataContext.Provider value={value}>{children}</CanvasGasDataContext.Provider>;
}

/**
 * Read canvas-level auto-swap coverage. Outside the provider (public
 * read-only viewer, template view) returns AUTO_SWAP_COVERAGE_DISABLED so
 * gas-warning surfaces fall back to "auto-swap unavailable" rather than
 * crashing.
 */
export function useCanvasGasData(): CanvasGasDataValue {
  return useContext(CanvasGasDataContext);
}
